import { useState } from 'react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useInvitations,
  useRevokeInvitation,
  useResendInvitation,
  type InvitationOut,
} from '../api/invitations-api';

const STATUS_FILTERS = ['all', 'pending', 'accepted', 'expired', 'revoked'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const STATUS_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  accepted: 'default',
  expired: 'destructive',
  revoked: 'secondary',
};

export function InvitationsTab() {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const { data: invitations, isLoading } = useInvitations(
    filter === 'all' ? undefined : filter,
  );
  const revokeInvitation = useRevokeInvitation();
  const resendInvitation = useResendInvitation();

  async function handleRevoke(invitation: InvitationOut) {
    try {
      await revokeInvitation.mutateAsync(invitation.id);
      toast.success('Invitation revoked');
    } catch {
      toast.error('Failed to revoke invitation');
    }
  }

  async function handleResend(invitation: InvitationOut) {
    try {
      await resendInvitation.mutateAsync(invitation.id);
      toast.success('Invitation resent');
    } catch {
      toast.error('Failed to resend invitation');
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const list = invitations ?? [];

  return (
    <div className="flex flex-col gap-4">
      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              filter === s
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Invitee</TableHead>
              <TableHead className="hidden md:table-cell">Invited By</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Sent</TableHead>
              <TableHead className="w-[120px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell>
                  <div className="font-medium">{inv.name}</div>
                  <div className="text-xs text-muted-foreground">{inv.email}</div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                  {inv.invited_by_name ?? '\u2014'}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{inv.role_name ?? '\u2014'}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_BADGE_VARIANT[inv.status] ?? 'outline'}>
                    {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                  </Badge>
                </TableCell>
                <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(inv.created_at), { addSuffix: true })}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {(inv.status === 'pending' || inv.status === 'expired') && (
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline disabled:opacity-50"
                        disabled={resendInvitation.isPending}
                        onClick={() => handleResend(inv)}
                      >
                        Resend
                      </button>
                    )}
                    {inv.status === 'pending' && (
                      <button
                        type="button"
                        className="text-xs text-destructive hover:underline disabled:opacity-50"
                        disabled={revokeInvitation.isPending}
                        onClick={() => handleRevoke(inv)}
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No invitations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Showing {list.length} invitations
      </p>
    </div>
  );
}
